import React, { useState } from "react";
import {
  BarChart,
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { MonthsAbr } from "./dateConstants";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useTranslation } from "react-i18next";

type ChartProps = {
  transactions: Array<{
    date: Date;
    value: number;
  }>;
};

const Chart: React.FC<ChartProps> = ({ transactions }) => {
  const lastNoMonths = 12;
  const [chartType, setChartType] = useState<"BAR" | "LINE">("BAR");
  const [monthsToPast, setMonthsToPast] = useState(0);

  // generate "lastUnits" buckets with from and to interval
  const buckets = new Array(lastNoMonths).fill({}).map((_, i) => {
    const pastMonthIndex = lastNoMonths - i - 1;

    const from = new Date();
    from.setMonth(from.getMonth() - pastMonthIndex - monthsToPast);
    from.setDate(1);
    from.setHours(0);
    from.setMinutes(0);
    from.setSeconds(0);
    from.setMilliseconds(0);

    const to = new Date();
    to.setMonth(to.getMonth() - pastMonthIndex + 1 - monthsToPast);
    to.setDate(0);
    to.setHours(23);
    to.setMinutes(59);
    to.setSeconds(59);
    to.setMilliseconds(99);

    return {
      label: `${MonthsAbr[from.getMonth()]} ${from.getFullYear()}`,
      from,
      to,
      transactions: [] as { value: number; date: Date }[],
      income: 0,
      expense: 0,
    };
  });

  // for each transaction decide if that transaction is in some of these bucket and if yes, add
  transactions.forEach((t) => {
    buckets.forEach((b) => {
      if (t.date > b.from && t.date < b.to) {
        b.transactions.push(t);
        if (t.value > 0) {
          b.income += t.value;
        } else {
          b.expense -= t.value;
        }
      }
    });
  });

  const { t } = useTranslation();

  return (
    <>
      <h1 className="text-3xl mt-10 mb-2">
        📊 {t("Income")} / {t("Expenses")}
      </h1>
      <div className="bg-gray-50 shadow-xl p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row items-center mb-8">
          <select
            className="border border-gray-400 rounded-md px-5 py-1 mr-10 w-8/12 sm:w-auto text-center sm:text-left"
            value={chartType}
            onChange={(e) => setChartType(e.target.value as "LINE" | "BAR")}
          >
            <option value="BAR">{t("Bar chart")}</option>
            <option value="LINE">{t("Line chart")}</option>
          </select>
          <div className="flex items-center mt-4 sm:mt-0">
            <div
              onClick={() => setMonthsToPast((p) => p + 12)}
              className=" w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center cursor-pointer"
            >
              <MdKeyboardArrowLeft />
            </div>
            <span className="mx-4">
              {MonthsAbr[buckets[0].from.getMonth()]}{" "}
              {buckets[0].from.getFullYear()} -{" "}
              {MonthsAbr[buckets[buckets.length - 1].to.getMonth()]}{" "}
              {buckets[buckets.length - 1].to.getFullYear()}
            </span>
            <div
              onClick={() => setMonthsToPast((p) => p - 12)}
              className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center cursor-pointer"
            >
              <MdKeyboardArrowRight />
            </div>
          </div>
        </div>
        <div>
          {chartType === "BAR" ? (
            <ResponsiveContainer width="100%" minHeight={400}>
              <BarChart data={buckets}>
                <Tooltip />
                <CartesianGrid horizontal={true} vertical={false} />
                <XAxis dataKey="label" />
                <YAxis />
                <Bar dataKey="income" fill="#5adc8b" />
                <Bar dataKey="expense" fill="#e47d7d" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" minHeight={400}>
              <LineChart data={buckets}>
                <Tooltip />
                <CartesianGrid horizontal={true} vertical={false} />
                <XAxis dataKey="label" />
                <YAxis />
                <Line dataKey="income" stroke="#5adc8b" />
                <Line dataKey="expense" stroke="#e47d7d" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default Chart;
